import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { blip } from "@/lib/audio";
import MsgAttachments from "@/components/MsgAttachments";
import { Radio, User, Send, Paperclip, X, Loader2, Check, CheckCheck } from "lucide-react";
import { t, initials, isUnreadMsg, MSG_FILE_ACCEPT } from "./constants";

// MESSAGES view — group/broadcast + private DM threads with attachments,
// unread counters and read receipts. Mobile: thread list collapses to a
// horizontal strip above the conversation.
export const MdtMessagesView = ({ messages, allUnits, myUnit, user, load, markTyping, offline = false, enqueueMessage }) => {
  const [msgTab, setMsgTab] = useState("group");
  const [msgTo, setMsgTo] = useState("");
  const [msgText, setMsgText] = useState("");
  const [msgFiles, setMsgFiles] = useState([]);
  const [msgSending, setMsgSending] = useState(false);

  const isUnread = (m) => isUnreadMsg(m, myUnit, user);
  const groupMsgs = messages.filter((m) => m.scope !== "unit");
  const dispatchMsgs = messages.filter((m) => (m.to_unit === myUnit && !m.from_unit) || (m.from_unit === myUnit && m.to_unit === "DISPATCH"));
  const dmMsgs = msgTo === "DISPATCH" ? dispatchMsgs : msgTo ? messages.filter((m) => m.from_unit === msgTo || m.to_unit === msgTo) : groupMsgs;
  const unreadGroup = groupMsgs.filter(isUnread).length;
  const unreadDispatch = dispatchMsgs.filter(isUnread).length;
  const unreadFromUnit = (u) => messages.filter((m) => m.from_unit === u && isUnread(m)).length;
  const msgUnreadTotal = messages.filter(isUnread).length;

  // Land the officer on whatever actually needs reading: if all unread traffic is
  // in DIRECT threads (e.g. a dispatch DM), auto-open that thread on mount so the
  // badge count clears instead of appearing "stuck".
  useEffect(() => {
    if (unreadGroup > 0 || msgUnreadTotal === 0) return;
    const firstUnread = messages.find((m) => m.scope === "unit" && isUnread(m));
    if (!firstUnread) return;
    setMsgTab("direct");
    setMsgTo(firstUnread.from_unit || "DISPATCH");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Viewing a thread marks its messages read (server-side, drives all counters).
  useEffect(() => {
    const ids = dmMsgs.filter(isUnread).map((m) => m.id);
    if (!ids.length) return;
    client.post("/messages/read", { ids }).catch(() => {});
  }, [msgTo, msgTab, messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const stageFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const valid = [];
    for (const f of picked) {
      if (f.size > 15_000_000) { toast.error(`${f.name} is too large (max 15MB)`); continue; }
      valid.push(f);
    }
    setMsgFiles((prev) => [...prev, ...valid].slice(0, 10));
  };
  const removeStaged = (idx) => setMsgFiles((prev) => prev.filter((_, i) => i !== idx));
  const sendMsg = async () => {
    if (!msgText.trim() && msgFiles.length === 0) return;
    if (msgSending) return;
    const base = msgTo ? { channel: "unit", scope: "unit", to_unit: msgTo } : { scope: "broadcast" };
    if ((offline || !navigator.onLine) && enqueueMessage && msgFiles.length === 0) {
      enqueueMessage({ ...base, text: msgText, from_unit: myUnit, attachments: [], msg_type: "text" });
      setMsgText(""); return;
    }
    setMsgSending(true);
    try {
      const atts = [];
      for (const f of msgFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await client.post("/messages/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        atts.push(data);
      }
      await client.post("/messages", { ...base, text: msgText, from_unit: myUnit, attachments: atts, msg_type: atts.length ? "document" : "text" });
      setMsgText(""); setMsgFiles([]); blip(); load();
    } catch (e) {
      if (!e.response && enqueueMessage && msgFiles.length === 0) { enqueueMessage({ ...base, text: msgText, from_unit: myUnit, attachments: [], msg_type: "text" }); setMsgText(""); }
      else toast.error(e.response?.data?.detail || "Message failed to send");
    }
    finally { setMsgSending(false); }
  };

  const Unread = ({ n, testid }) => n > 0 ? (
    <span data-testid={testid} className="ml-auto min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white font-mono text-[9px] font-bold flex items-center justify-center shrink-0">{n > 99 ? "99+" : n}</span>
  ) : null;

  return (
    <div className="flex-1 flex flex-col md:flex-row overflow-hidden rounded-lg border" style={{ background: t.panel, borderColor: t.border }} data-testid="mdt-messages-view">
      <div className="w-full md:w-52 shrink-0 border-b md:border-b-0 md:border-r overflow-y-auto max-h-40 md:max-h-none" style={{ borderColor: t.border }}>
        <div className="flex gap-1 p-2 border-b" style={{ borderColor: t.border }}>
          <button data-testid="mdt-msgtab-group" onClick={() => { setMsgTab("group"); setMsgTo(""); }} className="flex-1 px-2 py-1.5 text-[10px] font-head rounded flex items-center justify-center gap-1 transition-colors" style={msgTab === "group" ? { background: t.blue, color: "#fff" } : { background: t.surface, color: t.dim }}><Radio size={12} /> GROUP{unreadGroup > 0 && <span className="min-w-[15px] h-4 px-1 rounded-full bg-[#DC2626] text-white font-mono text-[9px] font-bold flex items-center justify-center">{unreadGroup}</span>}</button>
          <button data-testid="mdt-msgtab-direct" onClick={() => { setMsgTab("direct"); if (!msgTo) setMsgTo("DISPATCH"); }} className="flex-1 px-2 py-1.5 text-[10px] font-head rounded flex items-center justify-center gap-1 transition-colors" style={msgTab === "direct" ? { background: t.green, color: "#fff" } : { background: t.surface, color: t.dim }}><User size={12} /> DIRECT{(msgUnreadTotal - unreadGroup) > 0 && <span className="min-w-[15px] h-4 px-1 rounded-full bg-[#DC2626] text-white font-mono text-[9px] font-bold flex items-center justify-center">{msgUnreadTotal - unreadGroup}</span>}</button>
        </div>
        {msgTab === "group" ? (
          <>
            <div className="px-3 py-1.5 font-mono text-[9px] uppercase" style={{ color: t.dim }}>Group / Broadcast</div>
            <button data-testid="mdt-dm-broadcast" onClick={() => setMsgTo("")} className="w-full text-left px-3 py-2 text-sm font-head border-l-2 flex items-center gap-2" style={msgTo === "" ? { borderColor: t.blue, background: t.surface, color: "#6ea8ff" } : { borderColor: "transparent", color: t.text }}><Radio size={14} /> Dispatch / All Units</button>
          </>
        ) : (
          <>
            <div className="px-3 py-1.5 font-mono text-[9px] uppercase" style={{ color: t.dim }}>Direct Message</div>
            <button data-testid="mdt-dm-dispatch" onClick={() => setMsgTo("DISPATCH")} className="w-full text-left px-3 py-2 text-sm font-head font-bold border-l-2 flex items-center gap-2" style={msgTo === "DISPATCH" ? { borderColor: t.gold, background: t.surface, color: t.gold } : { borderColor: "transparent", color: t.gold }}><Radio size={14} /> DISPATCH <Unread n={unreadDispatch} testid="mdt-dm-dispatch-unread" /></button>
            {allUnits.filter((u) => u.unit_id !== myUnit).map((u) => (
              <button key={u.unit_id} data-testid={`mdt-dm-${u.unit_id}`} onClick={() => setMsgTo(u.unit_id)} className="w-full text-left px-3 py-1.5 text-sm font-mono border-l-2 flex items-center gap-2" style={msgTo === u.unit_id ? { borderColor: t.green, background: t.surface, color: "#4ade80" } : { borderColor: "transparent", color: t.text }}><User size={12} /> {u.unit_id} <Unread n={unreadFromUnit(u.unit_id)} /></button>))}
          </>
        )}
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 py-2 border-b font-head text-sm font-bold" style={{ borderColor: t.border }}>{msgTo ? `Private — ${myUnit} ⇄ ${msgTo}` : "Dispatch / All Units"}</div>
        <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="mdt-dm-thread">
          {dmMsgs.map((m) => {
            const mine = m.from_unit === myUnit;
            const who = m.from_unit || m.from_name || "??";
            return (
              <div key={m.id} className={`flex gap-2 max-w-[92%] md:max-w-[80%] ${mine ? "ml-auto flex-row-reverse" : ""}`}>
                <div data-testid="msg-sender-avatar" title={who}
                  className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-head text-[10px] font-bold border"
                  style={mine
                    ? { background: t.blue, borderColor: "#3b82f6", color: "#fff" }
                    : { background: t.head, borderColor: m.priority ? "#f87171" : t.border, color: m.priority ? "#f87171" : "#67e8f9" }}>
                  {initials(who)}
                </div>
                <div className={mine ? "text-right" : ""}>
                  <div className="rounded px-3 py-2 border inline-block text-left" style={{ background: mine ? t.surface : t.head, borderColor: t.border }}>
                    <div className="font-mono text-[10px] mb-0.5 flex items-center gap-1" style={{ color: t.dim }}>
                      <span style={{ color: mine ? "#6ea8ff" : "#67e8f9" }}>{who}</span> · {new Date(m.ts).toLocaleTimeString("en-GB", { hour12: false })}{m.to_unit ? ` → ${m.to_unit}` : ""}
                      {mine && (() => {
                        const seen = (m.reads || []).filter((n) => n !== user?.name);
                        return seen.length
                          ? <span data-testid={`mdt-msg-seen-${m.id}`} title={`Seen by ${seen.join(", ")}`} className="flex items-center" style={{ color: "#4ade80" }}><CheckCheck size={12} /></span>
                          : <span data-testid={`mdt-msg-sent-${m.id}`} title="Delivered — not yet seen" className="flex items-center" style={{ color: t.dim }}><Check size={12} /></span>;
                      })()}
                    </div>
                    {m.text && <div className="text-sm whitespace-pre-wrap break-words" style={{ color: m.priority ? "#f87171" : t.text }}>{m.text}</div>}
                    <MsgAttachments attachments={m.attachments} />
                  </div>
                </div>
              </div>
            );
          })}
          {dmMsgs.length === 0 && <div className="text-xs font-mono" style={{ color: t.dim }}>No messages.</div>}
        </div>
        {msgFiles.length > 0 && (
          <div data-testid="mdt-staged-files" className="flex flex-wrap gap-2 px-2 pt-2 border-t" style={{ borderColor: t.border }}>
            {msgFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-1.5 pl-2 pr-1 py-1 rounded border text-[11px] font-mono" style={{ background: t.surface, borderColor: t.border, color: t.text }}>
                <Paperclip size={11} style={{ color: t.dim }} />
                <span className="max-w-[130px] truncate">{f.name}</span>
                <button data-testid={`mdt-staged-remove-${i}`} onClick={() => removeStaged(i)} title="Remove file"
                  className="ml-0.5 rounded-full p-0.5 hover:bg-[#DC2626] hover:text-white transition-colors" style={{ color: t.dim }}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 p-2 border-t items-center" style={{ borderColor: t.border }}>
          <label data-testid="mdt-message-attach" title="Attach files (images, PDF, docs)"
            className="shrink-0 px-2.5 py-2 rounded border cursor-pointer flex items-center" style={{ borderColor: t.border, color: t.dim }}>
            <Paperclip size={16} />
            <input type="file" multiple accept={MSG_FILE_ACCEPT} className="hidden" onChange={stageFiles} />
          </label>
          <input data-testid="mdt-message-input" value={msgText} onChange={(e) => { setMsgText(e.target.value); markTyping(); }} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder={msgTo ? `Private message to ${msgTo}…` : "Message dispatch / all units…"} className="flex-1 min-w-0 border px-3 py-2 text-sm outline-none font-mono rounded" style={{ background: t.surface, borderColor: t.border, color: t.text }} />
          <button data-testid="mdt-message-send" onClick={sendMsg} disabled={msgSending} className="px-3 sm:px-4 py-2 rounded text-white flex items-center gap-1.5 font-head text-sm disabled:opacity-50" style={{ background: t.blue }}>{msgSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}<span className="hidden sm:inline"> Send</span></button>
        </div>
      </div>
    </div>
  );
};
